import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { reportsApi, type ControlEvaluationsResponse } from '../../services/reportsApi';
import {
  formatConfirmed,
  formatRate,
  formatUsableHours,
  formatWindow,
  isDescriptiveCopy,
  resultTestId,
  type ControlEvaluation,
} from '../../domain/safetyCases/controlEvaluationPresenter';

type ControlEvaluationPanelProps = {
  safetyCaseId: string;
};

function SideBlock({ title, side }: { title: string; side: ControlEvaluation['before'] }) {
  return (
    <Box data-testid={`control-evaluation-${title.toLowerCase()}`}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{formatWindow(side)}</Typography>
      <Typography variant="body2" color="text.secondary">{formatConfirmed(side?.confirmed)}</Typography>
      <Typography variant="body2" color="text.secondary">{formatUsableHours(side?.usable_hours)}</Typography>
      <Typography variant="body2" color="text.secondary">{formatRate(side?.rate)}</Typography>
    </Box>
  );
}

export const ControlEvaluationPanel: React.FC<ControlEvaluationPanelProps> = ({
  safetyCaseId,
}) => {
  const [payload, setPayload] = useState<ControlEvaluationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const listed = await reportsApi.listControlEvaluations(safetyCaseId);
    if (listed.control_evaluations && listed.control_evaluations.length > 0) {
      setPayload(listed);
      return;
    }
    const recorded = await reportsApi.recordControlEvaluation(safetyCaseId, {});
    setPayload(recorded);
  }, [safetyCaseId]);

  useEffect(() => {
    void load().catch((err: { message?: string }) => {
      setError(err?.message || 'Unable to load Control Evaluation.');
    });
  }, [load]);

  const evaluation = useMemo<ControlEvaluation | null>(() => {
    const row = payload?.control_evaluation || (payload?.control_evaluations || []).slice(-1)[0];
    return (row as ControlEvaluation) || null;
  }, [payload]);

  const copy = String(evaluation?.customer_copy || '');
  const limitations = evaluation?.limitations || [];
  const crossing = (evaluation?.comparability_reason_codes || []).some(
    (code) => code === 'CONTROL_BOUNDARY_CROSSED' || code === 'crossing_rejected_as_after',
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="control-evaluation-panel">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Control Evaluation
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!evaluation && payload ? (
          <Typography variant="body2" color="text.secondary" data-testid="control-evaluation-missing">
            No Control Evaluation is recorded yet. A governed before measurement and a clean
            post-Control follow-up are required.
          </Typography>
        ) : null}
        {evaluation ? (
          <Stack spacing={1.5}>
            <Box data-testid="control-evaluation-control">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Control</Typography>
              <Typography variant="body2" color="text.secondary">
                {evaluation.control?.description || 'Recorded Control'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Implemented {evaluation.control?.implemented_at || 'time not recorded'}
              </Typography>
            </Box>
            <SideBlock title="Before" side={evaluation.before} />
            <SideBlock title="After" side={evaluation.after} />
            <Box data-testid={resultTestId(evaluation.customer_result)}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Observed result</Typography>
              <Typography variant="body2">
                {evaluation.customer_result_label || evaluation.customer_result}
              </Typography>
              {copy && isDescriptiveCopy(copy) ? (
                <Typography variant="body2" color="text.secondary" data-testid="control-evaluation-copy">
                  {copy}
                </Typography>
              ) : null}
            </Box>
            {evaluation.comparability_verdict === 'comparable_with_limitations' ? (
              <Alert severity="info" data-testid="control-evaluation-limitations">
                {limitations[0] || 'Comparison is authorized with limitations.'}
              </Alert>
            ) : null}
            {crossing ? (
              <Alert severity="info" data-testid="control-evaluation-crossing-reason">
                This Scan spans the time the Control was implemented. It is retained in the Case
                history but is not used as a clean before/after comparison.
              </Alert>
            ) : null}
            {evaluation.customer_result === 'not_comparable' && limitations.length ? (
              <Typography variant="body2" color="text.secondary" data-testid="control-evaluation-not-comparable-reason">
                {limitations[0]}
              </Typography>
            ) : null}
            {evaluation.claim_percentage_allowed !== true ? (
              <Typography variant="body2" color="text.secondary" data-testid="control-evaluation-no-percent">
                No percentage change is shown because a claim percentage is not authorized.
              </Typography>
            ) : null}
          </Stack>
        ) : null}
        <Box data-testid="control-evaluation-no-horizon-picker" hidden />
      </CardContent>
    </Card>
  );
};

export default ControlEvaluationPanel;
